import { faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import debounce from 'debounce-promise';
import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { CustomField, IFetchPersonFieldValue, TPersonCustomDataValue } from '@feathr/blackbox';
import { FieldCollection, FieldDataType } from '@feathr/blackbox';
import type { IOptionsSelectOption, ISelectOption } from '@feathr/components';
import {
  AsyncCreatableSelectElement,
  Button,
  Checkbox,
  DatePicker as Picker,
  Fieldset,
  Input,
  NumberInput as NumberInputComponent,
  Spinner,
} from '@feathr/components';
import { StoresContext } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT, moment, TimeFormat } from '@feathr/hooks';
import { isWretchError, wretch } from '@feathr/rachis';

import type { ICustomDataObject } from './PersonCustomData';

import * as styles from './PersonDetail.css';

interface IProps {
  addCustomField: (field: CustomField) => void;
  customData: IObservableArray<ICustomDataObject>;
  datumKey: string;
  handleValueChange: (datumKey: string, newValue: TPersonCustomDataValue) => void;
  value: TPersonCustomDataValue;
}

interface IOption {
  id: string;
  name: string;
}

function PersonCustomDataInputs({
  customData,
  datumKey,
  value,
  handleValueChange,
}: IProps): JSX.Element {
  const { CustomFields } = useContext(StoresContext);
  const { t } = useTranslation();

  const fields = CustomFields.list({
    filters: {
      collection: FieldCollection.Person,
      is_archived__ne: true,
      u_key: datumKey,
    },
    pagination: { page_size: 1000 },
  });
  const customField = fields.isPending ? undefined : fields.models[0];

  function handleBooleanChange(newValue?: boolean): void {
    handleValueChange(datumKey, newValue);
  }

  function handleStringChange(newValue?: string): void {
    handleValueChange(datumKey, newValue ?? '');
  }

  function handleNumberChange(newValue?: number): void {
    handleValueChange(datumKey, newValue ?? '');
  }

  function handleDateChange(newValue?: string): void {
    handleValueChange(datumKey, moment.utc(newValue).format(TimeFormat.isoDateTime));
  }

  const TextInput = (
    <Input
      key={'str'}
      label={!!customField && customField.get('u_key')}
      onChange={handleStringChange}
      type={'text'}
      value={value as string}
    />
  );

  const NumberInput = (
    <NumberInputComponent
      key={'number'}
      label={!!customField && customField.get('u_key')}
      onChange={handleNumberChange}
      value={value as number}
    />
  );

  const BooleanInput = (
    <div>
      <label>{!!customField && customField.get('u_key')}</label>
      <Checkbox
        key={'boolean'}
        label={t('True')}
        onChange={handleBooleanChange}
        value={value as boolean}
      />
    </div>
  );

  const DatePicker = (
    <Picker
      key={'date'}
      label={!!customField && customField.get('u_key')}
      onDateStrChange={handleDateChange}
      selected={new Date(value as string)}
    />
  );

  function getDefaultListOptions(): IOption[] {
    const listValues = customData
      .filter((datum) => datum.value instanceof Array && datum.value.length > 0)
      .map((obj) => obj.value) as string[][];
    return listValues.length > 0
      ? listValues
          .reduce((acc, curr) => {
            const values = [...curr].filter((v) => !acc.includes(v));
            return acc.concat(values);
          })
          .map((str) => ({ name: str, id: str }))
      : [];
  }

  // TODO: Move this function to persons.ts.
  const getListOptions = useRef<(inputValue: string) => Promise<IOptionsSelectOption[]>>(
    debounce(async (query = '') => {
      const headers = CustomFields.getHeaders();
      const url = `${BLACKBOX_URL}persons/custom.${
        !!customField && customField.get('f_key')
      }/values/?q=${encodeURIComponent(query)}`;
      const response = await wretch<IFetchPersonFieldValue[]>(url, {
        headers,
        method: 'GET',
      });
      if (isWretchError(response)) {
        throw response.error;
      }
      return response.data.filter((v) => !!v.value).map((v) => ({ name: v.value, id: v.value }));
    }, DEFAULT_DEBOUNCE_WAIT),
  ).current;

  function handleListChange(options: ISelectOption[]): void {
    handleValueChange(
      datumKey,
      options.map(({ id }) => id),
    );
  }

  function formatCreateLabel(inputValue: string): string {
    return t('Create new field: {{fieldname}}', { fieldname: inputValue });
  }

  function createOption(inputValue: string): ISelectOption {
    return { name: inputValue, id: inputValue };
  }

  function getNewOptionData(inputValue: string): ISelectOption {
    return { name: inputValue, id: inputValue };
  }

  const ListInput = (
    <AsyncCreatableSelectElement
      className={styles.root}
      createOption={createOption}
      defaultOptions={getDefaultListOptions()}
      defaultValue={getDefaultListOptions().filter(({ id }) =>
        (value instanceof Array ? value : []).includes(id),
      )}
      formatCreateLabel={formatCreateLabel}
      getNewOptionData={getNewOptionData}
      isMulti={true}
      key={'list'}
      label={!!customField && customField.get('u_key')}
      loadOptions={getListOptions}
      onChange={handleListChange}
      value={(value instanceof Array ? value : []).map((val) => ({ name: val, id: val }))}
    />
  );

  const inputMap: Record<FieldDataType, JSX.Element> = {
    [FieldDataType.str]: TextInput,
    [FieldDataType.float]: NumberInput,
    [FieldDataType.int]: NumberInput,
    [FieldDataType.bool]: BooleanInput,
    [FieldDataType.date]: DatePicker,
    [FieldDataType.list]: ListInput,
  };

  async function handleClick(): Promise<void> {
    const selectedField = customData.find(({ key }) => key === datumKey);
    customData.remove(selectedField!);
  }

  if (fields.isPending) {
    return <Spinner size={32} />;
  }

  return (
    <Fieldset direction={'row'}>
      <div className={styles.customData}>
        {inputMap[customField!.get('data_type')]}
        <Button
          disabled={value === undefined}
          onClick={handleClick}
          title={t('Remove custom data')}
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </div>
    </Fieldset>
  );
}

export default observer(PersonCustomDataInputs);
